/**
 * Sprint UX-2: Live Decision Flow — home do hub Operação.
 * 3 faixas:
 *  1) 4 cards macro
 *  2) Pipeline horizontal de 8 etapas
 *  3) Últimas 20 decisões
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Users, ShieldAlert, Activity, Gauge, ArrowRight, MessageSquare,
  Filter, Brain, Scale, AlertTriangle, GitBranch, Send, Sparkles, Wrench,
  Radio, Clock3, CheckCircle2, PauseCircle, SlidersHorizontal,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LoadingSkeleton } from "@/components/states/LoadingSkeleton";
import { useUIMode } from "@/hooks/useUIMode";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { CortexLiveNodes } from "@/components/cortex/CortexLiveNodes";

const ACTION_LABELS: Record<string, string> = {
  send: "Enviar",
  shorten: "Encurtar",
  rephrase: "Reescrever",
  replace_with_neutral: "Resposta neutra",
  ask_clarification: "Pedir esclarecimento",
  block: "Bloquear",
  log_and_silence: "Silenciar",
  escalate_to_human: "Escalar",
};

type StageStatus = "active" | "recent" | "idle";

const PIPELINE_STAGES = [
  { key: "inbound", label: "Inbound", icon: MessageSquare, route: "/cortex-os?tab=pipeline", match: ["inbound.", "webhook.", "message."] },
  { key: "classification", label: "Classificação", icon: Filter, route: "/cortex-os?tab=pipeline", match: ["intent.", "classification.", "gating."] },
  { key: "memory", label: "Memória", icon: Brain, route: "/memory?tab=auditoria", match: ["memory.", "metacog."] },
  { key: "policy", label: "Policy", icon: Scale, route: "/model-routing?tab=policies", match: ["policy.", "routing.", "model."] },
  { key: "inhibitor", label: "Inhibitor", icon: ShieldAlert, route: "/governanca/forense", match: ["inhibitor.", "inhibition.", "metacog.calibration.applied"] },
  { key: "final", label: "Final", icon: Send, route: "/governanca/forense", match: ["dispatch.", "send.", "response.", "reaffirmation."] },
] as const;

interface MacroMetrics {
  active_groups: number;
  threat_count: number;
  inhibition_rate: number;
  avg_latency_ms: number;
}

interface DecisionRow {
  id: string;
  org_id: string;
  created_at: string;
  action: string;
  reason: string | null;
  group_state: string | null;
  arousal: number | null;
  conflict_score: number | null;
}

interface CortexEventRow {
  id: string;
  event_type: string;
  occurred_at: string;
  source: string | null;
  group_id: string | null;
  correlation_id: string | null;
  payload: unknown;
}

interface LiveStage {
  key: string;
  label: string;
  icon: (typeof PIPELINE_STAGES)[number]["icon"];
  route: string;
  status: StageStatus;
  count: number;
  lastEvent?: CortexEventRow;
}

function buildFlowStages(events: CortexEventRow[]): LiveStage[] {
  const now = Date.now();
  return PIPELINE_STAGES.map((stage) => {
    const matched = events.filter((event) => stage.match.some((prefix) => event.event_type.startsWith(prefix) || event.event_type === prefix));
    const lastEvent = matched[0];
    const ageMs = lastEvent ? now - new Date(lastEvent.occurred_at).getTime() : Number.POSITIVE_INFINITY;
    const status: StageStatus = ageMs <= 5 * 60_000 ? "active" : matched.length > 0 ? "recent" : "idle";
    return { ...stage, status, count: matched.length, lastEvent };
  });
}

const STAGE_STATUS_LABELS: Record<StageStatus, string> = {
  active: "ativo",
  recent: "recente",
  idle: "sem sinal",
};

const STAGE_STATUS_STYLES: Record<StageStatus, string> = {
  active: "border-primary bg-primary/10 text-primary",
  recent: "border-accent bg-accent/50 text-accent-foreground",
  idle: "border-border bg-muted/30 text-muted-foreground",
};

export default function Operacao() {
  const { isAdvanced } = useUIMode();
  const { data: orgId } = useCurrentOrgId();

  const { data: macro, isLoading: loadingMacro } = useQuery<MacroMetrics>({
    queryKey: ["operacao-macro", orgId],
    enabled: !!orgId,
    refetchInterval: 15_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const [groups, threats, inh] = await Promise.all([
        supabase.from("syra_groups").select("id", { count: "exact", head: true }).eq("org_id", orgId!).eq("active", true),
        (supabase as any)
          .from("cortex_events")
          .select("id", { count: "exact", head: true })
          .eq("org_id", orgId!)
          .like("event_type", "threat.%")
          .gte("occurred_at", since),
        supabase
          .from("inhibition_decisions")
          .select("action")
          .eq("org_id", orgId!)
          .gte("created_at", since)
          .limit(1000),
      ]);
      const decisions = (inh.data ?? []) as { action: string }[];
      const totalDec = decisions.length;
      const blocked = decisions.filter(
        (d) => d.action === "block" || d.action === "log_and_silence" || d.action === "replace_with_neutral",
      ).length;
      return {
        active_groups: groups.count ?? 0,
        threat_count: threats.count ?? 0,
        inhibition_rate: totalDec === 0 ? 0 : (blocked / totalDec) * 100,
        avg_latency_ms: 0,
      };
    },
  });

  const { data: decisions, isLoading: loadingDec } = useQuery({
    queryKey: ["operacao-decisions", orgId],
    enabled: !!orgId,
    refetchInterval: 10_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inhibition_decisions")
        .select("id, org_id, created_at, action, reason, group_state, arousal, conflict_score")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as DecisionRow[];
    },
  });

  const { data: events = [], isLoading: loadingEvents } = useQuery({
    queryKey: ["operacao-events", orgId],
    enabled: !!orgId,
    refetchInterval: 5_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("cortex_events")
        .select("id, event_type, occurred_at, source, group_id, correlation_id, payload")
        .eq("org_id", orgId!)
        .gte("occurred_at", since)
        .order("occurred_at", { ascending: false })
        .limit(80);
      if (error) throw error;
      return (data ?? []) as CortexEventRow[];
    },
  });

  const flowStages = useMemo(() => buildFlowStages(events), [events]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Operação · Fluxo de decisão</h1>
          <p className="text-sm text-muted-foreground">
            Visão em tempo real do que o sistema está decidindo, agora.
          </p>
        </div>
        {isAdvanced && (
          <Button variant="outline" size="sm" asChild>
            <Link to="/operacao/avancada">
              <Wrench className="h-4 w-4 mr-1" />
              Cockpit avançado
            </Link>
          </Button>
        )}
      </header>

      {/* Faixa 1 — 4 cards macro */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MacroCard
          label="Saúde dos grupos"
          value={loadingMacro ? "—" : String(macro?.active_groups ?? 0)}
          hint="Grupos ativos"
          icon={Users}
        />
        <MacroCard
          label="Risco reputacional"
          value={loadingMacro ? "—" : String(macro?.threat_count ?? 0)}
          hint="Ameaças nas últimas 24h"
          icon={ShieldAlert}
          tone={macro && macro.threat_count > 0 ? "warn" : "ok"}
        />
        <MacroCard
          label="Taxa de regulação"
          value={loadingMacro ? "—" : `${(macro?.inhibition_rate ?? 0).toFixed(1)}%`}
          hint="Respostas reguladas em 24h"
          icon={Activity}
        />
        <MacroCard
          label="Latência média"
          value={loadingMacro ? "—" : "~"}
          hint="Pipeline ponta a ponta"
          icon={Gauge}
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="h-4 w-4 text-primary" />
            Fluxo vivo de decisão
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {flowStages.map((stage) => (
              <LiveStageCard key={stage.key} stage={stage} loading={loadingEvents} />
            ))}
          </div>
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-sm font-medium">Últimos sinais do Cortex</span>
              <Badge variant="outline" className="gap-1">
                <Clock3 className="h-3 w-3" />
                1h
              </Badge>
            </div>
            {loadingEvents ? (
              <LoadingSkeleton />
            ) : events.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem eventos recentes para a organização ativa.</p>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {events.slice(0, 6).map((event) => (
                  <div key={event.id} className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{event.event_type}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.source ?? "cortex"} · {new Date(event.occurred_at).toLocaleTimeString("pt-BR")}
                      </p>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {event.correlation_id ? "trace" : "evento"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* A9-02 — Nó vivo? */}
      <CortexLiveNodes />


      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Pipeline de decisão
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {PIPELINE_STAGES.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2 shrink-0">
                <Link
                  to={s.route}
                  className="flex flex-col items-center justify-center gap-1 rounded-lg border bg-card px-4 py-3 min-w-[110px] hover:border-primary hover:bg-accent transition-colors"
                >
                  <s.icon className="h-5 w-5 text-primary" />
                  <span className="text-xs font-medium">{s.label}</span>
                </Link>
                {i < PIPELINE_STAGES.length - 1 && (
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Faixa 3 — Últimas 20 decisões */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Últimas 20 decisões</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingDec ? (
            <LoadingSkeleton />
          ) : !decisions || decisions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem decisões recentes.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Estado do grupo</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Trace</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {decisions.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(d.created_at).toLocaleTimeString("pt-BR")}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{ACTION_LABELS[d.action] ?? d.action}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{d.group_state ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                      {d.reason ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/governanca/forense?id=${d.id}`}>Ver</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface MacroCardProps {
  label: string;
  value: string;
  hint: string;
  icon: typeof Users;
  tone?: "ok" | "warn";
}

function MacroCard({ label, value, hint, icon: Icon, tone }: MacroCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
          {label}
        </CardTitle>
        <Icon className={`h-4 w-4 ${tone === "warn" ? "text-destructive" : "text-primary"}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      </CardContent>
    </Card>
  );
}

function LiveStageCard({ stage, loading }: { stage: LiveStage; loading: boolean }) {
  const Icon = stage.icon;
  const StatusIcon = stage.status === "active" ? CheckCircle2 : stage.status === "recent" ? SlidersHorizontal : PauseCircle;

  return (
    <Link
      to={stage.route}
      className={`rounded-md border p-3 transition-colors hover:border-primary hover:bg-accent/50 ${STAGE_STATUS_STYLES[stage.status]}`}
    >
      <div className="flex items-start justify-between gap-2">
        <Icon className="h-5 w-5 shrink-0" />
        <Badge variant="outline" className="bg-background/60 text-[11px]">
          {loading ? "…" : stage.count}
        </Badge>
      </div>
      <div className="mt-3 space-y-1">
        <p className="text-sm font-semibold text-foreground">{stage.label}</p>
        <p className="flex items-center gap-1 text-xs">
          <StatusIcon className="h-3 w-3" />
          {STAGE_STATUS_LABELS[stage.status]}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {stage.lastEvent ? stage.lastEvent.event_type : "aguardando evento"}
        </p>
      </div>
    </Link>
  );
}
